import { LogicContext } from '../types';
import { fetchPage } from '../../../model/page';
import {
  extractNavigationLinksFromNavComponent,
  getProductContext,
} from '../utils';
import { extractControllersData } from '../../controllers';
import config from '../../../config';
import { HOMEPAGE_BASE_PATH } from '../constants';
import { getErrorPageContent } from './getErrorPageContent';
import {
  CloseStrategyEnum,
  LaunchStrategyEnum,
} from '../../../types/gameTypes';
import { initializePersonalizedBanners } from './initializePersonalizedBanners';

export const pageIdToLayoutChange = async (
  context: LogicContext,
  event: { reload_layout?: boolean }
) => {
  const updates = [];
  const navigationLinks: string[] = [];
  const componentsMap: {
    [key: string]: { type: string; properties: object; children: string[] };
  } = {};
  let mainLayoutPath = context.mainLayoutPath;
  const trackingData = context.trackingData ? context.trackingData : {};
  const seo = context.seo || {};
  const segments = context.segments ? context.segments : [];
  let personalizedBanners: { selector: string; slug?: string }[] = [];
  let gameLaunchStrategies: { [k in LaunchStrategyEnum]: string[] }[] = [];
  let gameCloseStrategies: { [k in CloseStrategyEnum]: string[] }[] = [];

  if (context.isErrorPage)
    return {
      controllersData: {},
      navigationLinks: context.navigationLinks,
      sidebarLayout: false,
      updates: [],
      trackingData,
      seo,
      noScroll: false,
      segments,
      isErrorPage: true,
    };

  const product = getProductContext();
  if (!product) {
    const errorPage = getErrorPageContent();

    updates.push({
      type: 'initLayout',
      data: errorPage,
    });

    return {
      controllersData: {},
      navigationLinks: context.navigationLinks,
      sidebarLayout: false,
      updates: sortUpdates(updates),
      trackingData,
      seo,
      noScroll: false,
      segments,
      isErrorPage: true,
    };
  }

  const shouldRefetchLayout =
    context.initialRequest || event.reload_layout || context.isErrorPage;
  const newData = await fetchPage({
    title: product,
    path:
      context.currentPagePath.replace(config.base_path || '', '') ||
      HOMEPAGE_BASE_PATH,
    ...(shouldRefetchLayout && {
      include_layout: true,
    }),
    locale: config.locale,
    viewer_country: config.viewer_country,
    viewer_region: config.viewer_region,
    logged_in: context.user.loggedIn,
    base_path: config.base_path,
    accountID: context.user.userAccountNumber,
  });
  let isSidebarInLayout = false;

  if (!newData) {
    const errorPage = getErrorPageContent();

    updates.push({
      type: 'initLayout',
      data: errorPage,
    });

    return {
      controllersData: {},
      navigationLinks: context.navigationLinks,
      sidebarLayout: false,
      isSidebarInLayout,
      updates: sortUpdates(updates),
      trackingData,
      seo,
      noScroll: false,
      segments: [],
      isErrorPage: true,
    };
  }

  if (newData.trackingInfo) {
    Object.assign(trackingData, newData.trackingInfo);
  }

  if (newData.page) {
    Object.assign(componentsMap, newData.page.renderManifest.components);
    const pageMainTitle = newData.page?.trackingInfo?.SiteSubSection;
    isSidebarInLayout = !!Object.entries(
      newData.page.renderManifest.components
    ).find(([key, values]) => key === 'Modal' && values.children.length);
    Object.entries(componentsMap).forEach(([key, values]: [any, any]) => {
      let props = values.properties;
      if (props) props.pageTitle = pageMainTitle;
      updates.push(
        ...[
          {
            type: 'setComponentChildren',
            data: {
              path: `components.${key}.children`,
              value: values.children || {},
            },
          },
          {
            type: 'setLayoutProperty',
            data: {
              path: `components.${key}.properties`,
              value: props,
            },
          },
        ]
      );
    });

    if (context.initialRequest)
      updates.push({
        type: 'initLayout',
        data: { root: 'Layout', components: componentsMap },
      });
    updates.push({
      type: 'setComponentsMap',
      data: { value: componentsMap },
    });
    mainLayoutPath = `${config.base_path || ''}${newData.page.id}`;

    let pageTrackingData = newData.trackingInfo || {};
    if (context.changePageAction === 'VIEW_ALL') {
      const lastPage =
        context.previousPageArray[
          Math.max(context.previousPageArray.length - 1, 0)
        ]?.trackingData;

      pageTrackingData = {
        ...newData.trackingInfo,
        SiteSubSection:
          lastPage?.SiteSubSection ||
          lastPage?.SiteSectionCategory ||
          pageTrackingData.SiteSubSection ||
          null,
        SiteSectionCategory:
          pageTrackingData.SiteSectionCategory ||
          pageTrackingData.SiteSubSection,
      };
    }

    if (isSidebarInLayout) {
      pageTrackingData = {
        ...pageTrackingData,
        SiteSectionCategory:
          context.trackingData?.page?.SiteSectionCategory ||
          pageTrackingData['SiteSectionCategory'] ||
          'Featured',
      };
    }

    Object.assign(trackingData, { page: pageTrackingData });
    Object.assign(seo, { page: newData.page.seo });
    Object.assign(
      personalizedBanners,
      newData.page.renderManifest.personalizedBanners
    );
    Object.assign(
      gameLaunchStrategies,
      newData.page.renderManifest.gameLaunchStrategies
    );
    Object.assign(
      gameCloseStrategies,
      newData.page.renderManifest.gameCloseStrategies
    );
  }

  navigationLinks.push(
    ...extractNavigationLinksFromNavComponent(
      componentsMap.StaticNavigation,
      context.currentScreenSize
    )
  );

  const controllersData = extractControllersData(
    Object.entries(componentsMap),
    false,
    context.controllersData
  );

  personalizedBanners.length &&
    initializePersonalizedBanners(personalizedBanners);

  const oldUpdates =
    context.updates && context.updates.map(u => JSON.stringify(u));
  const currentUpdates = oldUpdates
    ? updates.filter(u => !oldUpdates.includes(JSON.stringify(u)))
    : updates;

  return {
    navigationLinks: navigationLinks.length
      ? navigationLinks
      : context.navigationLinks,
    currentTablesIdList: newData.properties.liveDealerTables || [],
    updates: sortUpdates(currentUpdates),
    controllersData,
    mainLayoutPath,
    isSidebarInLayout,
    pageTitle:
      newData.page?.properties?.pageTitle || newData.properties.pageTitle,
    segments: newData.properties.segments,
    trackingData,
    personalizedBanners,
    personalizedBannersInitialized: !!personalizedBanners.length,
    gameLaunchStrategies,
    gameCloseStrategies,
    seo,
    isErrorPage:
      newData.properties.status === 'NOT FOUND' ||
      newData.properties.status === 'NOT_FOUND',
    currentPagePath: context.mainLayoutPath || mainLayoutPath,
  };
};

function sortUpdates(arr: Array<{ type: string; data: any }>) {
  const updatesOrder = [
    'initLayout',
    'setComponentsMap',
    'setComponentChildren',
    'setLayoutProperty',
    'showLayoutComponent',
    'hideLayoutComponent',
  ];

  return arr.reduce(
    (
      acc: Array<{ type: string; data: any }>,
      item: { type: string; data: any }
    ) => {
      const itemIndex = updatesOrder.indexOf(item.type);
      const itemsBeforeCurrent = acc.filter(accItem => {
        const accItemIndex = updatesOrder.indexOf(accItem.type);
        return -1 < accItemIndex && accItemIndex <= itemIndex;
      });

      acc.splice(itemsBeforeCurrent.length, 0, item);

      return acc;
    },
    []
  );
}
